import * as React from 'react';
import { DefaultButton, Image, Stack, Text } from '@fluentui/react';
import SigninButton from './assets/signin.svg';
import TemplateExtenderLogo from './assets/templateextender.svg';
import { TenantContext, UserContext } from './contexts/ContextWrapper';
import { useLocation, useNavigate } from 'react-router-dom';
import LoadingSession from './components/common/LoadingSession';
import { AuthContext } from './contexts/AuthContext';
import { useSession } from './hooks/useSession';
import { getNavigationAddress } from './helpers/NavigationHelper';
import SettingsCompany from './helpers/SettingsCompany';
import { IsVerbose } from './helpers/Console';
import './Login.css'; // Import the CSS file

// admin_login answers 404 when the signed-in account is not registered in our user database.
const ACCOUNT_MISSING_MESSAGE = 'Your account is not registered in 365|TemplateExtender. Contact your administrator or sign out and try another account.';
const SERVER_ERROR_MESSAGE = 'Server error occurred. Please try again later or contact support.';

const sessionErrorMessage = (status?: number): string =>
	status === 404 ? ACCOUNT_MISSING_MESSAGE : SERVER_ERROR_MESSAGE;

const Login: React.FC = () => {
	const { tenantContext, setTenantContext } = React.useContext(TenantContext);
	const { userContext, setUserContext } = React.useContext(UserContext);
	const { handleLogin, handleLogout, isSigningIn, isAuthenticated, accessToken, isInitializing } = React.useContext(AuthContext);
	const { LoginSession, sessionInitialized } = useSession();

	// Track whether Login has already attempted to initialize a session
	const hasAttemptedLogin = React.useRef(false);
	// Track if we encountered a server error to prevent infinite loops
	const [serverError, setServerError] = React.useState<string | null>(null);

	const navigate = useNavigate();
	const location = useLocation();

	// Destructure only the specific properties needed from contexts
	const tenantName = tenantContext.tenantName;
	const api = tenantContext.api;
	const userDomains = userContext.userData?.Domains;

	// Parse intended destination (api/domain/page) from the path that redirected here,
	// so we return the user to where they wanted to go instead of a default domain.
	const intendedFrom = (location.state as { from?: string } | null)?.from;
	const intendedTarget = React.useMemo(() => {
		if (!intendedFrom) return null;
		const segments = intendedFrom.split('/').filter(Boolean);
		const [apiSeg, domainSeg, pageSeg] = segments;
		if (!apiSeg || !domainSeg) return null;
		return { api: apiSeg, domain: domainSeg, page: pageSeg || 'home' };
	}, [intendedFrom]);

	const resolveDestination = React.useCallback(
		(domains: typeof userDomains, fallbackApi: string, fallbackTenant: string, fallbackPage: string): string => {
			if (intendedTarget && domains) {
				const match = domains.find(
					d => d.DomainName?.toLowerCase() === intendedTarget.domain.toLowerCase() &&
						d.APIName?.toLowerCase() === intendedTarget.api.toLowerCase()
				);
				if (match) {
					return `/${intendedTarget.api.toLowerCase()}/${intendedTarget.domain.toLowerCase()}/${intendedTarget.page.toLowerCase()}`;
				}
			}
			return getNavigationAddress(fallbackTenant, fallbackApi, domains || [], fallbackPage);
		},
		[intendedTarget]
	);

	React.useEffect(() => {
		if (isInitializing) {
			IsVerbose() && console.log('⏳ Auth still initializing, waiting for token...');
			return;
		}

		// Prevent infinite loops by checking if a login session is already in progress or if we have a server error
		if (sessionInitialized || hasAttemptedLogin.current || serverError) {
			return;
		}

		if (isAuthenticated) {
			if (tenantName && api) { // Context has tenant and api
				const page: string = localStorage.getItem('selectedPage') || 'home';
				// Check if user data (specifically domains) is missing
				if (!userDomains && accessToken && !isSigningIn) {
					IsVerbose() && console.log('Login.tsx: useEffect: Context complete, but missing user domains. Fetching...');
					hasAttemptedLogin.current = true;
					LoginSession(
						(userData, api, tenantName) => {
							setTenantContext(prev => ({ ...prev, api: api, tenantName: tenantName, settings: prev.settings || {} as SettingsCompany }));
							setUserContext(prev => ({ ...prev, userData: userData }));
							navigate(resolveDestination(userData.Domains, api, tenantName, page));
						},
						(error, status) => {
							console.error('Login.tsx: LoginSession failed (fetching user domains):', error);
							setServerError(sessionErrorMessage(status));
							hasAttemptedLogin.current = false;
						},
						accessToken
					);
				} else if (userDomains) { // All data present
					IsVerbose() && console.log('Login.tsx: useEffect: Authenticated and all context/user data present. Navigating.');
					navigate(resolveDestination(userDomains, api, tenantName, page));
				}
			} else if (!isSigningIn && accessToken) { // Authenticated, but missing tenantName or api. Have token.
				IsVerbose() && console.log('Login.tsx: useEffect: Authenticated, but missing tenant/api context. Attempting LoginSession.');
				// Mark that we've attempted login to prevent loops
				hasAttemptedLogin.current = true;
				LoginSession(
					(userData, api, tenantName) => {
						setTenantContext(prev => ({ ...prev, api: api, tenantName: tenantName, settings: {} as SettingsCompany }));
						setUserContext(prev => ({ ...prev, userData: userData }));
						const page: string = localStorage.getItem('selectedPage') || 'home';
						navigate(resolveDestination(userData.Domains, api, tenantName, page));
					},
					(error, status) => {
						console.error('Login.tsx: LoginSession failed (fetching initial context):', error);
						setServerError(sessionErrorMessage(status));
						hasAttemptedLogin.current = false;
					},
					accessToken
				);
			} else if (!isSigningIn && !accessToken) {
				console.warn('Login.tsx: useEffect: Authenticated, not signing in, but no access token. Waiting for token or user action.');
				// Do nothing here, wait for accessToken to be populated by AuthContext or for user to click login.
			}
		}
		// No 'else' for !isAuthenticated, because the component renders the sign-in button in that case.
	}, [
		isAuthenticated,
		isSigningIn,
		isInitializing,
		accessToken,
		tenantName,
		api,
		userDomains,
		LoginSession,
		navigate,
		setTenantContext,
		setUserContext,
		sessionInitialized,
		serverError,
		resolveDestination
	]);
	// Reset the login attempt flag and server error if auth state changes
	React.useEffect(() => {
		hasAttemptedLogin.current = false;
		setServerError(null);
		IsVerbose() && console.log('Reset login attempt flag and server error due to auth state change');
	}, [isAuthenticated, accessToken]);

	// Show loading indicator if any login/session process is in progress (but not if there's a server error)
	if (!serverError && (isInitializing || isAuthenticated || isSigningIn || sessionInitialized)) {
		return (
			<LoadingSession logout={handleLogout} />
		)
	}

	// Show error message if server error occurred
	if (serverError) {
		return (
			<div className="login-container">
				<Stack styles={{ root: { height: '100%' } }}>
					<Stack grow horizontalAlign='center' verticalAlign='center' tokens={{ childrenGap: 20 }} styles={{ root: { height: '100%', width: '100%' } }}>
						<div className="login-logo-container">
							<Image alt='TemplateExtender' src={TemplateExtenderLogo} width={'100%'} height={'100%'} />
						</div>
						<Text variant='mediumPlus' style={{ color: '#a4262c', padding: '10px', }}>
							{serverError}
						</Text>
						<Stack horizontal tokens={{ childrenGap: 10 }}>
							{serverError !== ACCOUNT_MISSING_MESSAGE && (
								<DefaultButton onClick={() => { setServerError(null); hasAttemptedLogin.current = false; }}>
									Retry
								</DefaultButton>
							)}
							<DefaultButton onClick={handleLogout}>
								Logout
							</DefaultButton>
						</Stack>
					</Stack>
					<Stack.Item align='center'>
						<div className="login-copyright" ><a href='https://www.rehngruppen.se' target='_blank' rel="noopener noreferrer" className="login-copyright-link">© Rehngruppen AB {new Date().getFullYear()}</a></div>
					</Stack.Item>
				</Stack>
			</div>
		);
	}

	return (
		<div className="login-container">
			<Stack styles={{ root: { height: '100%' } }}>
				<Stack grow horizontalAlign='center' verticalAlign='center' tokens={{ childrenGap: 20 }} styles={{ root: { height: '100%', width: '100%' } }}>
					<div className="login-logo-container">
						<Image alt='TemplateExtender' src={TemplateExtenderLogo} width={'100%'} height={'100%'} />
					</div>
					<Image alt='Signin' src={SigninButton} onClick={() => handleLogin()} />
				</Stack>
				<Stack.Item align='center'>
					<div className="login-copyright" ><a href='https://www.rehngruppen.se' target='_blank' rel="noopener noreferrer" className="login-copyright-link">© Rehngruppen AB {new Date().getFullYear()}</a></div>
				</Stack.Item>
			</Stack>
		</div>
	);
}

export default Login;